import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import InputField from "../components/InputField";
import CountryDropdown from "../components/CountryDropdown";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import ResultsSection from "../components/ResultsSection";
import RateLimitStatus from "../components/RateLimitStatus";
import useAuthToken from "../hooks/useAuthToken";
import rateLimitService from "../services/rateLimitService";
import { showToast } from "../components/ToastNotification";
import { FaHashtag } from "react-icons/fa";
import analytics from "../services/analytics";

const RiskAssessmentPage = () => {
  const { fetchWithAuth } = useAuthToken();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get timeout configuration from environment
  const RISK_ASSESSMENT_TIMEOUT = parseInt(process.env.REACT_APP_RISK_ASSESSMENT_TIMEOUT_SECONDS || "135") * 1000;
  const TIMEOUT_MINUTES = Math.ceil(RISK_ASSESSMENT_TIMEOUT / 60000);

  // State declarations first
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("Portugal");
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const [progress, setProgress] = useState({ step: '', percentage: 0, timeElapsed: 0 });
  const [companyData, setCompanyData] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [rateLimitBlocked, setRateLimitBlocked] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const timerRef = useRef(null);

  // Check for payment success on component mount
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const plan = searchParams.get('plan');
    const period = searchParams.get('period');
    const price = searchParams.get('price');
    
    if (paymentSuccess === 'true' && plan) {
      // Track successful subscription checkout
      analytics.trackSubscriptionCheckoutComplete(
        plan,
        period || 'unknown',
        price ? parseFloat(price) : 0
      );
      
      // Show success message
      showToast(`Successfully subscribed to ${plan} plan! You can now use the risk assessment tool.`, "success");
      
      // Clean URL by removing query parameters
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Update progress with elapsed time
  const updateProgress = (step, percentage) => {
    const elapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    setProgress({ step, percentage, timeElapsed: elapsed });
    setCurrentStep(step);
  };

  // Enhanced fetch with timeout and progress tracking
  const fetchWithTimeout = async (url, options = {}, timeout = 30000, stepName = '') => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    try {
      if (stepName) {
        setCurrentStep(stepName);
      }
      
      const response = await fetchWithAuth(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      if (error.name === 'AbortError') {
        throw new Error(`${stepName} timed out after ${timeout/1000} seconds`);
      }
      throw error;
    }
  };


  const generateAssessment = async () => {
    if (!company || !location) {
      showToast("Missing fiscal number or location", "error");
      analytics.trackRiskAssessmentError('basic', 'missing_fields');
      return;
    }

    // Clear previous results before starting new assessment
    setRiskAssessment(null);
    setCompanyData(null);
    setProgress({ step: '', percentage: 0, timeElapsed: 0 });
    setCurrentStep("");

    // Check rate limits before proceeding
    const limits = rateLimitService.checkLimits();
    if (!limits.allowed) {
      const statusMessage = rateLimitService.getStatusMessage();
      showToast(statusMessage.message, "error");
      analytics.trackRiskAssessmentError('basic', 'rate_limit_exceeded');
      return;
    }

    // Track assessment start
    analytics.trackRiskAssessmentStart('basic');

    try {
      setLoading(true);
      const now = Date.now();
      setStartTime(now);
      updateProgress("Validating subscription...", 10);
      
      // Start timer to update elapsed time every second
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - now) / 1000);
        setProgress(prev => ({ 
          ...prev, 
          timeElapsed: elapsed,
          // Update message for longer waits
          step: elapsed > 90 ? "Almost ready! Final analysis in progress..." : prev.step
        }));
      }, 1000);

      const res = await fetchWithTimeout(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/validate-service-access`,
        { method: "POST" },
        10000,
        "Validating subscription..."
      );
      const data = await res.json();

      localStorage.setItem("plan_status", JSON.stringify(data));

      if (!data.allowed) {
        showToast(data.reason || "Access denied", "error");
        analytics.trackRiskAssessmentError('basic', 'access_denied');
        setLoading(false);
        return;
      }

      updateProgress("Searching company information...", 25);

      // 1?? First API: Get company links
      const searchResponse = await fetchWithTimeout(
        `${process.env.REACT_APP_API_BASE_URL}/api/search/company/${company}-${location}`,
        {},
        15000,
        "Searching company information..."
      );
      const searchData = await searchResponse.json();
      if (!searchResponse.ok) {
        showToast("Failed to search for company.", "error");
        analytics.trackRiskAssessmentError('basic', 'company_search_failed');
        throw new Error("Failed to search for company.");
      }

      updateProgress("Scraping company data (may take 30-60 seconds)...", 35);

      // 2?? Second API: Scrape company data (this is the slow one!)
      const scrapeResponse = await fetchWithTimeout(
        `${process.env.REACT_APP_API_BASE_URL}/api/scrape/company`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ links: searchData.links }),
        },
        90000, // 90 seconds timeout for scraping
        "Scraping company data..."
      );

      let companyDataResult;
      if (!scrapeResponse.ok) {
        // Try to get the error message from the response
        try {
          const errorData = await scrapeResponse.json();
          const errorMessage = errorData.detail || "Failed to scrape company data.";
          showToast(errorMessage, "error");
          throw new Error(errorMessage);
        } catch (e) {
          showToast("Failed to scrape company data.", "error");
          throw new Error("Failed to scrape company data.");
        }
      }
      
      companyDataResult = await scrapeResponse.json();
      if (JSON.stringify(companyDataResult) === "{}") {
        showToast("No company data found.", "error");
        analytics.trackRiskAssessmentError('basic', 'no_company_data');
        throw new Error("No company data found.");
      }
      
      updateProgress("Generating company location map...", 65);

      // 3?? Third API: Generate map image
      const mapResponse = await fetchWithTimeout(
        `${process.env.REACT_APP_API_BASE_URL}/api/maps/company`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            latitude: companyDataResult.company_data.latitude,
            longitude: companyDataResult.company_data.longitude,
          }),
        },
        20000, // 20 seconds timeout for map generation
        "Generating location map..."
      );

      if (!mapResponse.ok || !mapResponse) {
        showToast("Failed to fetch company location map.", "error");
        throw new Error("Failed to fetch company location map.");
      }
      const mapBlob = await mapResponse.blob();

      const map_image_url = URL.createObjectURL(mapBlob);

      companyDataResult = {
        company_data: {
          ...companyDataResult.company_data,
          fiscal_number: company,
          map_image: map_image_url,
        },
      };
      setCompanyData(companyDataResult.company_data);
      updateProgress(`Performing AI risk analysis (may take up to ${TIMEOUT_MINUTES} minutes)...`, 80);

      // 4?? Fourth API: Get AI Risk Assessment - Send Image as FormData
      const formData = new FormData();
      formData.append("image", mapBlob, "company_map.png");

      // Build query params including coordinates if available
      const queryParams = new URLSearchParams({
        company: company,
        location: location,
        company_name: companyDataResult.company_data.name,
        address: companyDataResult.company_data.address
        //user_id: localStorage.getItem("user_id") || sessionStorage.getItem("user_id"),
      });
      
      // Add coordinates if available
      if (companyDataResult.company_data.latitude) {
        queryParams.append("latitude", companyDataResult.company_data.latitude);
      }
      if (companyDataResult.company_data.longitude) {
        queryParams.append("longitude", companyDataResult.company_data.longitude);
      }

      const openAIResponse = await fetchWithTimeout(
        `${process.env.REACT_APP_API_BASE_URL}/api/openai/company?${queryParams.toString()}`,
        {
          method: "POST",
          body: formData,
        },
        RISK_ASSESSMENT_TIMEOUT,
        `Analyzing company risks (this may take up to ${TIMEOUT_MINUTES} minutes)...`
      );

      // Handle backend rate limit response
      if (openAIResponse.status === 429) {
        const errorData = await openAIResponse.json();
        showToast(errorData.user_message || "Rate limit exceeded", "error");
        analytics.trackRiskAssessmentError('basic', 'backend_rate_limit');
        return;
      }

      // Handle backend timeout response
      if (openAIResponse.status === 408) {
        const errorData = await openAIResponse.json();
        showToast(errorData.detail || "Analysis timed out - please try again", "error");
        analytics.trackRiskAssessmentError('basic', 'analysis_timeout');
        return;
      }

      if (!openAIResponse.ok) {
        const errorText = await openAIResponse.text();
        console.error("OpenAI API Error:", {
          status: openAIResponse.status,
          statusText: openAIResponse.statusText,
          body: errorText
        });
        showToast(`Failed to generate risk assessment. Status: ${openAIResponse.status}`, "error");
        throw new Error(`Failed to generate risk assessment. Status: ${openAIResponse.status}`);
      }

      const riskData = await openAIResponse.json();
      
      updateProgress("Assessment completed!", 100);
      
      // Record successful request
      rateLimitService.recordRequest();
      setRiskAssessment(riskData);
      showToast("Successfully generated risk assessment.");
      
      // Track successful completion with duration
      const duration = (Date.now() - startTime) / 1000;
      analytics.trackRiskAssessmentComplete('basic', duration);

      setCurrentStep("");
    } catch (error) {
      console.error("Risk Assessment Error:", error);
      
      if (error.message && error.message.includes("timed out")) {
        showToast(error.message, "error");
        analytics.trackRiskAssessmentError('basic', 'timeout');
      } else {
        showToast(error.message || error, "error");
        analytics.trackRiskAssessmentError('basic', 'general_error');
      }
    } finally {
      setLoading(false);
      // Reset progress state on completion/error
      setProgress({ step: '', percentage: 0, timeElapsed: 0 });
      setCurrentStep("");
      // Clear the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };


  return (
    <div className="max-w-4xl mx-auto p-8 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">Start Your Assessment</h2>
      <p className="text-gray-600">
        Provide the necessary information below to generate a comprehensive risk
        assessment report.
      </p>


      <InputField
        label="Company Fiscal Number"
        type="text"
        placeholder="Enter company fiscal number"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        required
        icon={FaHashtag}
      />
      <CountryDropdown
        label="Company Location"
        value={location}
        onChange={setLocation}
        required
        placeholder="Select a country"
      />

      <RateLimitStatus onLimitChange={setRateLimitBlocked} />
      
      <Button 
        text="Generate Risk Assessment" 
        onClick={generateAssessment}
        disabled={rateLimitBlocked}
      />

      {loading && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <Spinner loading={loading} message={progress.step || currentStep} />
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>{progress.step}</span>
              <span>{progress.percentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>
            <div className="mt-2 text-sm text-gray-500 text-center">
              Time elapsed: {progress.timeElapsed} seconds
              {progress.percentage < 100 && progress.timeElapsed > 30 && (
                <div className="text-orange-600 mt-1">
                  ⏱️ This is taking longer than usual. Please be patient...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {!loading && riskAssessment && (
        <ResultsSection result={riskAssessment} company_data={companyData} />
      )}
    </div>
  );
};

export default RiskAssessmentPage;
